import { useState, type FormEvent } from 'react';
import { DEFAULT_AVATAR } from '@srcbattle/shared';
import { api, ApiRequestError } from '../api.ts';
import { useSession } from '../session.tsx';
import { Button, Card, Field } from '../components/ui.tsx';
import { AvatarPicker } from '../components/AvatarPicker.tsx';
import { ThemePicker } from '../components/ThemePicker.tsx';

export function ProfileSetup() {
  const { completeProfile, theme, changeTheme } = useSession();
  const [displayName, setDisplayName] = useState('');
  const [avatar, setAvatar] = useState<string>(DEFAULT_AVATAR);
  const [error, setError] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function uploadAvatar(file: File) {
    setBusy(true);
    setAvatarError(null);
    try {
      const { user } = await api.uploadAvatar(file);
      setAvatar(user.avatarValue);
    } catch (caught) {
      setAvatarError(
        caught instanceof ApiRequestError ? caught.message : 'That picture would not upload.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await completeProfile({ displayName, avatarValue: avatar });
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-4 py-12">
      <header className="text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Who are you?</h1>
      </header>

      <Card>
        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <Field
            label="Display name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Zavier"
            maxLength={24}
            autoFocus
            required
            {...(error ? { error } : {})}
          />

          <AvatarPicker
            value={avatar}
            busy={busy}
            onPickEmoji={setAvatar}
            onPickFile={(file) => void uploadAvatar(file)}
            error={avatarError}
          />

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium text-text">Theme</legend>
            <ThemePicker theme={theme} onChange={changeTheme} />
          </fieldset>

          <Button type="submit" loading={busy}>
            That's me
          </Button>
        </form>
      </Card>
    </main>
  );
}
